// Admin: author care packages. A box is anchored to ONE merchant and built
// from that merchant's real, available items. Pick a merchant → its catalog
// loads → check items + set quantities → name the box → save. The exact
// total is computed live from real prices.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Minus, Loader2, Gift, Store, MapPin, Trash2, Pencil, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState, StatusPill } from '../../components/shared/primitives';
import { Modal } from '../../components/shared/Modal';
import { Button } from '../../components/stripe-ui/Button';
import { ADMIN_NAV } from '../_navs';
import {
  admin, merchantItems, adminCarePackages,
  type AdminCarePackage, type AdminMerchantRow, type MerchantItem,
} from '../../lib/api';
import { iconFor } from '../../lib/icons';
import { formatHtgMinor, formatUsdFromHtgMinor } from '../../lib/money';

const ICON_OPTIONS = ['ShoppingBasket', 'GraduationCap', 'Baby', 'HeartPulse', 'Soup', 'Gift', 'Pill', 'Coffee', 'Utensils', 'Package'];

export function AdminCarePackagesPage() {
  const listQ = useQuery({ queryKey: ['admin', 'care-packages'], queryFn: adminCarePackages.list });
  const [editing, setEditing] = useState<AdminCarePackage | 'new' | null>(null);

  return (
    <PortalShell
      nav={ADMIN_NAV}
      activeId="packages"
      title="Care packages"
      subtitle="Curated boxes shown to every customer."
      actions={<Button onClick={() => setEditing('new')} leadingIcon={<Plus className="w-3.5 h-3.5" />}>New box</Button>}
    >
      <Card padding="p-0">
        {listQ.isLoading ? (
          <div className="text-center py-12 text-[13px] text-[var(--color-muted)]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
        ) : (listQ.data ?? []).length === 0 ? (
          <EmptyState icon={Gift} title="No care packages yet" body="Create the first one-click box from a merchant's catalog."
            action={<Button onClick={() => setEditing('new')} leadingIcon={<Plus className="w-3.5 h-3.5" />}>New box</Button>} />
        ) : (
          <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
            <thead className="text-[10.5px] uppercase tracking-wider text-[var(--color-muted)] bg-[var(--color-panel)]">
              <tr>
                <th className="text-left py-2.5 px-5 font-medium">Box</th>
                <th className="text-left py-2.5 px-3 font-medium">Merchant</th>
                <th className="text-left py-2.5 px-3 font-medium">Items</th>
                <th className="text-left py-2.5 px-3 font-medium">Total</th>
                <th className="text-left py-2.5 px-3 font-medium">Status</th>
                <th className="py-2.5 px-5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-hairline)]">
              {(listQ.data ?? []).map((p) => <PackageRow key={p.id} p={p} onEdit={() => setEditing(p)} />)}
            </tbody>
          </table>
        )}
      </Card>

      {editing && (
        <PackageEditor
          key={editing === 'new' ? 'new' : editing.id}
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </PortalShell>
  );
}

function PackageRow({ p, onEdit }: { p: AdminCarePackage; onEdit: () => void }) {
  const qc = useQueryClient();
  const Icon = iconFor(p.system_icon);
  const del = useMutation({
    mutationFn: () => adminCarePackages.remove(p.id),
    onSuccess: () => { toast.success(`Deleted "${p.name}"`); qc.invalidateQueries({ queryKey: ['admin', 'care-packages'] }); qc.invalidateQueries({ queryKey: ['storefront', 'packages'] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <tr className="hover:bg-[var(--color-panel)]">
      <td className="py-3 px-5">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex w-8 h-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0"><Icon className="w-4 h-4" /></span>
          <div className="min-w-0">
            <p className="font-semibold text-[var(--color-ink)]">{p.name}</p>
            {p.subtitle && <p className="text-[11.5px] text-[var(--color-secondary-2)] truncate max-w-xs">{p.subtitle}</p>}
          </div>
        </div>
      </td>
      <td className="py-3 px-3 text-[var(--color-body)]">
        <p className="inline-flex items-center gap-1 text-[12px]"><Store className="w-3 h-3 text-[var(--color-muted)]" /> {p.merchant?.business_name ?? '—'}</p>
        {p.merchant?.city && <p className="inline-flex items-center gap-1 text-[11px] text-[var(--color-secondary-2)]"><MapPin className="w-3 h-3" /> {p.merchant.city.name}</p>}
      </td>
      <td className="py-3 px-3 text-[var(--color-secondary)]">{p.items_count ?? 0}</td>
      <td className="py-3 px-3 font-mono text-[var(--color-ink)]">{p.approx_htg_minor != null ? formatHtgMinor(p.approx_htg_minor) : '—'}</td>
      <td className="py-3 px-3"><StatusPill status={p.active ? 'active' : 'draft'} /></td>
      <td className="py-3 px-5">
        <div className="flex items-center justify-end gap-1">
          <button onClick={onEdit} className="p-1.5 rounded-md hover:bg-[var(--color-panel)] text-[var(--color-secondary-2)]" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
          <button
            onClick={() => { if (window.confirm(`Delete "${p.name}"?`)) del.mutate(); }}
            className="p-1.5 rounded-md hover:bg-[var(--color-danger-bg)] text-[var(--color-muted)] hover:text-[var(--color-danger-fg)]" title="Delete"
            disabled={del.isPending}
          ><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </td>
    </tr>
  );
}

// ─── Editor ──────────────────────────────────────────────────

interface Line { qty: number; notes?: string }

function PackageEditor({ existing, onClose }: { existing: AdminCarePackage | null; onClose: () => void }) {
  const qc = useQueryClient();

  const merchantsQ = useQuery({ queryKey: ['admin', 'merchants'], queryFn: () => admin.merchants() });
  const approvedMerchants = useMemo(
    () => (merchantsQ.data ?? []).filter((m) => m.status === 'approved'),
    [merchantsQ.data],
  );

  const [merchantId, setMerchantId] = useState<number | ''>(existing?.merchant_id ?? '');
  const [name, setName]         = useState(existing?.name ?? '');
  const [subtitle, setSubtitle] = useState(existing?.subtitle ?? '');
  const [icon, setIcon]         = useState(existing?.system_icon ?? 'ShoppingBasket');
  const [audience, setAudience] = useState(existing?.audience ?? '');
  const [active, setActive]     = useState(existing?.active ?? true);
  // merchant_item_id -> { qty, notes }
  const [selected, setSelected] = useState<Map<number, Line>>(() => {
    const m = new Map<number, Line>();
    for (const it of existing?.items ?? []) {
      if (it.merchant_item?.id) m.set(it.merchant_item.id, { qty: Number(it.default_quantity) });
    }
    return m;
  });
  const [search, setSearch] = useState('');

  // Default the merchant once the approved list loads (create mode only).
  useEffect(() => {
    if (merchantId === '' && !existing && approvedMerchants.length > 0) setMerchantId(approvedMerchants[0]!.user_id);
  }, [approvedMerchants, merchantId, existing]);

  const itemsQ = useQuery({
    queryKey: ['merchant', merchantId, 'items', 'all'],
    queryFn:  () => merchantItems.browse(Number(merchantId)),
    enabled:  merchantId !== '',
  });

  // Changing merchant in CREATE mode clears the selection (items belong to one
  // merchant). In edit mode we keep the loaded selection.
  const onMerchantChange = (v: number | '') => {
    setMerchantId(v);
    setSelected(new Map());
  };

  const setQty = (id: number, qty: number) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (qty <= 0) next.delete(id);
      else next.set(id, { ...(next.get(id) ?? {}), qty });
      return next;
    });
  };

  const items = itemsQ.data ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? items.filter((i) => i.name.toLowerCase().includes(s)) : items;
  }, [items, search]);

  const total = useMemo(() => {
    let t = 0;
    for (const it of items) {
      const line = selected.get(it.id);
      if (line) t += it.price_htg_minor * line.qty;
    }
    return t;
  }, [items, selected]);

  const save = useMutation({
    mutationFn: () => {
      if (!merchantId) throw new Error('Pick a merchant.');
      if (!name.trim()) throw new Error('Give the box a name.');
      if (selected.size === 0) throw new Error('Add at least one item.');
      const payload = {
        merchant_id: Number(merchantId),
        name: name.trim(),
        subtitle: subtitle.trim() || null,
        system_icon: icon,
        audience: audience.trim() || null,
        active,
        items: Array.from(selected.entries()).map(([merchant_item_id, line]) => ({ merchant_item_id, quantity: line.qty })),
      };
      return existing ? adminCarePackages.update(existing.id, payload) : adminCarePackages.create(payload);
    },
    onSuccess: () => {
      toast.success(existing ? 'Box updated' : 'Box created');
      qc.invalidateQueries({ queryKey: ['admin', 'care-packages'] });
      qc.invalidateQueries({ queryKey: ['storefront', 'packages'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      width="xl"
      title={existing ? `Edit "${existing.name}"` : 'New care package'}
      subtitle="Anchored to one merchant; built from their in-stock items."
      footer={
        <>
          <div className="mr-auto text-[12px] text-[var(--color-secondary-2)]">
            {selected.size} item{selected.size === 1 ? '' : 's'} · <span className="font-mono font-semibold text-[var(--color-ink)]">{formatHtgMinor(total)}</span>
            <span className="text-[var(--color-muted)]"> (≈ {formatUsdFromHtgMinor(total)})</span>
          </div>
          <Button tone="secondary" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={save.isPending}>
            {existing ? 'Save changes' : 'Create box'}
          </Button>
        </>
      }
    >
      <div className="grid md:grid-cols-2 gap-5">
        {/* Left — box meta */}
        <div className="space-y-3">
          <Field label="Merchant">
            <select
              value={merchantId === '' ? '' : String(merchantId)}
              onChange={(e) => onMerchantChange(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={merchantsQ.isLoading || approvedMerchants.length === 0}
              className={selectCls}
            >
              {approvedMerchants.length === 0
                ? <option value="">No approved merchants</option>
                : approvedMerchants.map((m: AdminMerchantRow) => (
                    <option key={m.user_id} value={m.user_id}>{m.business_name}{m.city?.name ? ` · ${m.city.name}` : ''}</option>
                  ))}
            </select>
          </Field>

          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="e.g. Weekly Groceries" maxLength={120} />
          </Field>

          <Field label="Subtitle">
            <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className={inputCls} placeholder="One-line description" maxLength={160} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Icon">
              <select value={icon} onChange={(e) => setIcon(e.target.value)} className={selectCls}>
                {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Audience tag">
              <input value={audience} onChange={(e) => setAudience(e.target.value)} className={inputCls} placeholder="family, new_baby…" maxLength={64} />
            </Field>
          </div>

          <label className="inline-flex items-center gap-2 text-[12.5px] text-[var(--color-body)] pt-1">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="rounded border-[var(--color-border)] text-brand-600 focus:ring-brand-500/30" />
            Active (visible on the storefront)
          </label>

          <div className="rounded-lg bg-[var(--color-panel)] border border-[var(--color-hairline)] p-3">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">Preview</p>
            <div className="mt-2 flex items-center gap-2">
              {(() => { const I = iconFor(icon); return <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><I className="w-4 h-4" /></span>; })()}
              <div>
                <p className="text-[13.5px] font-semibold text-[var(--color-ink)]">{name || 'Untitled box'}</p>
                <p className="text-[11px] text-[var(--color-secondary-2)]">{selected.size} items · {formatHtgMinor(total)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right — item picker */}
        <div>
          <Field label="Items (from this merchant's catalog)">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-[var(--color-muted)]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items…" className={`${inputCls} pl-8`} />
            </div>
          </Field>

          <div className="mt-2 border border-[var(--color-border)] rounded-lg max-h-[42vh] overflow-y-auto divide-y divide-[var(--color-hairline)]">
            {merchantId === '' ? (
              <p className="text-center py-8 text-[12.5px] text-[var(--color-muted)]">Pick a merchant first.</p>
            ) : itemsQ.isLoading ? (
              <p className="text-center py-8 text-[12.5px] text-[var(--color-muted)]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading catalog…</p>
            ) : filtered.length === 0 ? (
              <p className="text-center py-8 text-[12.5px] text-[var(--color-muted)]">No items match.</p>
            ) : (
              filtered.map((it) => <ItemPickRow key={it.id} it={it} qty={selected.get(it.id)?.qty ?? 0} setQty={setQty} />)
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function ItemPickRow({ it, qty, setQty }: { it: MerchantItem; qty: number; setQty: (id: number, qty: number) => void }) {
  const Icon = iconFor(it.system_icon);
  const on = qty > 0;
  return (
    <div className={`flex items-center gap-3 px-3 py-2 ${on ? 'bg-brand-50/40' : ''}`}>
      <span className="inline-flex w-7 h-7 items-center justify-center rounded-md bg-[var(--color-panel)] text-[var(--color-secondary-2)] shrink-0"><Icon className="w-3.5 h-3.5" /></span>
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-medium text-[var(--color-ink)] truncate">{it.name}</p>
        <p className="text-[10.5px] text-[var(--color-secondary-2)]">{it.unit} · {formatHtgMinor(it.price_htg_minor)}</p>
      </div>
      {on ? (
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setQty(it.id, qty - 1)} className="p-1 rounded hover:bg-white text-[var(--color-secondary-2)]"><Minus className="w-3 h-3" /></button>
          <span className="w-6 text-center text-[12px] font-mono font-semibold">{qty}</span>
          <button onClick={() => setQty(it.id, qty + 1)} className="p-1 rounded hover:bg-white text-[var(--color-secondary-2)]"><Plus className="w-3 h-3" /></button>
        </div>
      ) : (
        <button onClick={() => setQty(it.id, 1)} className="px-2 py-1 rounded-md bg-brand-600 text-white text-[11px] font-semibold hover:bg-brand-700 shrink-0 inline-flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add
        </button>
      )}
    </div>
  );
}

// ─── tiny field helpers (local) ──────────────────────────────

const inputCls = 'w-full px-3 py-2 text-[13px] bg-white border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400';
const selectCls = inputCls + ' disabled:bg-[var(--color-panel)] disabled:text-[var(--color-muted)]';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
